/**
 * March Line Renderer - Shows path lines from selected units to the command target
 */

import * as THREE from 'three';
import type { World } from '@/core/ecs/World';
import { ComponentTypes } from '@/core/ecs/Component';
import type { PositionComponent } from '@/game/components/Position';
import type { MovementComponent } from '@/game/components/Movement';
import type { Entity } from '@/types';
import { getSurfaceY } from '@/rendering/terrain/TerrainGenerator';
import { EventBus } from '@/core/events/EventBus';

interface MarchLine {
  mesh: THREE.Mesh;
  entity: Entity;
}

const LINE_COLOR = 0x00ff44;
const LINE_WIDTH = 0.12;

export class MarchLineRenderer {
  private scene: THREE.Scene;
  private world: World | null = null;
  private lines: Map<Entity, MarchLine> = new Map();
  private animationOffset = 0;

  // The single command target point (where user clicked)
  private commandTarget: { x: number; z: number; y: number } | null = null;
  private commandEntities: Set<Entity> = new Set();

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    // When a move command is issued, record the target point
    EventBus.on('command:move', (data: { x: number; z: number; unitCount: number }) => {
      this.commandTarget = {
        x: data.x,
        z: data.z,
        y: getSurfaceY(data.x, data.z) + 0.2,
      };
    });

    // When an attack command is issued, record the target point
    EventBus.on('command:attack', (data: { targetId: number; unitCount: number; x?: number; z?: number }) => {
      if (data.x !== undefined && data.z !== undefined) {
        this.commandTarget = {
          x: data.x,
          z: data.z,
          y: getSurfaceY(data.x, data.z) + 0.2,
        };
      }
    });
  }

  setWorld(world: World): void {
    this.world = world;
  }

  update(deltaTime: number, selectedUnits: Set<Entity>): void {
    if (!this.world || !selectedUnits) return;

    // Animate dash pattern
    this.animationOffset += deltaTime * 3;

    // Clear command target if no units selected
    if (selectedUnits.size === 0) {
      this.commandTarget = null;
      this.clearAllLines();
      return;
    }

    // Check if any selected unit is still moving to player command
    let hasMovingUnit = false;
    for (const entity of selectedUnits) {
      const movement = this.world.getComponent<MovementComponent>(entity, ComponentTypes.Movement);
      if (movement?.isPlayerCommand && movement.targetX !== null) {
        hasMovingUnit = true;
        break;
      }
    }

    // If no units are moving or no command target, clear lines
    if (!hasMovingUnit || !this.commandTarget) {
      this.clearAllLines();
      return;
    }

    // Update or create lines for selected units
    for (const entity of selectedUnits) {
      const pos = this.world.getComponent<PositionComponent>(entity, ComponentTypes.Position);
      const movement = this.world.getComponent<MovementComponent>(entity, ComponentTypes.Movement);

      if (!pos || !movement || !movement.isPlayerCommand || movement.finalTargetX === null) {
        this.removeLine(entity);
        continue;
      }

      // Calculate distance to command target
      const dx = this.commandTarget.x - pos.x;
      const dz = this.commandTarget.z - pos.z;
      const dist = Math.sqrt(dx * dx + dz * dz);

      // Remove line if unit is close to target
      if (dist < 1.0) {
        this.removeLine(entity);
        continue;
      }

      // Create or update line
      let marchLine = this.lines.get(entity);
      if (!marchLine) {
        marchLine = this.createLine(entity);
      }

      // All lines point to the same command target
      this.updateLineMesh(
        marchLine.mesh,
        pos.x, pos.y + 0.2, pos.z,
        this.commandTarget.x, this.commandTarget.y, this.commandTarget.z,
        dist
      );
    }

    // Clean up lines for deselected units
    for (const [entity] of this.lines) {
      if (!selectedUnits.has(entity)) {
        this.removeLine(entity);
      }
    }
  }

  private createLine(entity: Entity): MarchLine {
    const geometry = new THREE.BufferGeometry();

    const material = new THREE.MeshBasicMaterial({
      color: LINE_COLOR,
      transparent: true,
      opacity: 0.7,
      side: THREE.DoubleSide,
      depthWrite: false,
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.frustumCulled = false;
    this.scene.add(mesh);

    const marchLine = { mesh, entity };
    this.lines.set(entity, marchLine);
    return marchLine;
  }

  private updateLineMesh(
    mesh: THREE.Mesh,
    x1: number, y1: number, z1: number,
    x2: number, y2: number, z2: number,
    totalDist: number
  ): void {
    const dx = x2 - x1;
    const dz = z2 - z1;
    const len = Math.sqrt(dx * dx + dz * dz);

    if (len < 0.1) return;

    // Perpendicular direction for line width
    const perpX = (-dz / len) * LINE_WIDTH;
    const perpZ = (dx / len) * LINE_WIDTH;

    // Dashed line parameters
    const dashLen = 0.8;
    const gapLen = 0.4;
    const cycleLen = dashLen + gapLen;

    const vertices: number[] = [];
    const indices: number[] = [];

    let currentDist = this.animationOffset % cycleLen;
    let vertIndex = 0;

    while (currentDist < totalDist) {
      const dashStart = currentDist;
      const dashEnd = Math.min(currentDist + dashLen, totalDist);

      if (dashEnd > dashStart && dashStart >= 0) {
        const t1 = Math.max(0, dashStart / totalDist);
        const t2 = dashEnd / totalDist;

        const px1 = x1 + dx * t1;
        const py1 = y1 + (y2 - y1) * t1;
        const pz1 = z1 + dz * t1;

        const px2 = x1 + dx * t2;
        const py2 = y1 + (y2 - y1) * t2;
        const pz2 = z1 + dz * t2;

        vertices.push(
          px1 - perpX, py1, pz1 - perpZ,
          px1 + perpX, py1, pz1 + perpZ,
          px2 + perpX, py2, pz2 + perpZ,
          px2 - perpX, py2, pz2 - perpZ
        );

        indices.push(
          vertIndex, vertIndex + 1, vertIndex + 2,
          vertIndex, vertIndex + 2, vertIndex + 3
        );
        vertIndex += 4;
      }

      currentDist += cycleLen;
    }

    mesh.geometry.dispose();
    const newGeometry = new THREE.BufferGeometry();
    newGeometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    newGeometry.setIndex(indices);
    mesh.geometry = newGeometry;
  }

  private removeLine(entity: Entity): void {
    const existing = this.lines.get(entity);
    if (existing) {
      this.scene.remove(existing.mesh);
      existing.mesh.geometry.dispose();
      (existing.mesh.material as THREE.Material).dispose();
      this.lines.delete(entity);
    }
  }

  private clearAllLines(): void {
    for (const [entity] of this.lines) {
      this.removeLine(entity);
    }
  }

  dispose(): void {
    this.clearAllLines();
  }
}
